import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Upload as UploadIcon,
  PlayArrow as TestIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Preview as PreviewIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';

const TemplateTesting = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [generatedPDFs, setGeneratedPDFs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfsLoading, setPdfsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [registering, setRegistering] = useState(false);

  // Available template files
  const availableTemplates = [
    {
      templateId: 'fdr-template',
      name: 'Flight Data Record',
      description: 'Comprehensive FDR template for flight documentation',
      documentType: 'fdr',
      department: 'OPERATIONS',
      filePath: 'operations/fdr/FDR-template-v1.0.0.html',
      cssFilePath: 'operations/fdr/FDR-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'maintenance-report-template',
      name: 'Maintenance Report',
      description: 'Detailed maintenance work order report',
      documentType: 'maintenance-report',
      department: 'MAINTENANCE',
      filePath: 'maintenance/maintenance-report/Maintenance-Report-template-v1.0.0.html',
      cssFilePath: 'maintenance/maintenance-report/Maintenance-Report-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'duty-roster-template',
      name: 'Duty Roster',
      description: 'Crew duty roster and compliance tracking',
      documentType: 'duty-roster',
      department: 'OPERATIONS',
      filePath: 'operations/duty-roster/Duty-Roster-template-v1.0.0.html',
      cssFilePath: 'operations/duty-roster/Duty-Roster-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'aircraft-status-template',
      name: 'Aircraft Status Report',
      description: 'Aircraft status and maintenance overview',
      documentType: 'aircraft-status',
      department: 'GENERAL',
      filePath: 'general/aircraft-status/Aircraft-Status-Report-template-v1.0.0.html',
      cssFilePath: 'general/aircraft-status/Aircraft-Status-Report-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'generic-document-template',
      name: 'Generic Document',
      description: 'Flexible template for custom documents',
      documentType: 'generic',
      department: 'GENERAL',
      filePath: 'general/generic-document/Generic-Document-template-v1.0.0.html',
      cssFilePath: 'general/generic-document/Generic-Document-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'invoice-template',
      name: 'Invoice',
      description: 'Professional invoice template',
      documentType: 'invoice',
      department: 'GENERAL',
      filePath: 'general/invoice/Invoice-template-v1.0.0.html',
      cssFilePath: 'general/invoice/Invoice-template-v1.0.0.css',
      version: '1.0.0'
    },
    {
      templateId: 'itinerary-template',
      name: 'Itinerary',
      description: 'Flight itinerary for charter trips',
      documentType: 'itinerary',
      department: 'OPERATIONS',
      filePath: 'operations/itinerary/Itinerary-template-v1.0.0.html',
      cssFilePath: 'operations/itinerary/Itinerary-template-v1.0.0.css',
      version: '1.0.0'
    }
  ];

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/pdf/templates', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchGeneratedPDFs = useCallback(async () => {
    try {
      setPdfsLoading(true);
      const response = await axios.get('/api/v1/pdf/generated', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          limit: 100
        }
      });
      setGeneratedPDFs(response.data.pdfs || []);
    } catch (error) {
      console.error('Error fetching generated PDFs:', error);
      showToast('Failed to fetch generated PDFs', 'error');
    } finally {
      setPdfsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTemplates();
    if (tabValue === 2) {
      fetchGeneratedPDFs();
    }
  }, [tabValue, fetchTemplates, fetchGeneratedPDFs]);

  const handleViewPDF = (pdfId) => {
    const token = localStorage.getItem('token');
    const viewUrl = `${window.location.origin}/api/v1/pdf/${pdfId}/view?token=${encodeURIComponent(token)}`;
    window.open(viewUrl, '_blank');
  };

  const handleDownloadPDF = (pdfId) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${window.location.origin}/api/v1/pdf/${pdfId}/download?token=${encodeURIComponent(token)}`;
    window.open(downloadUrl, '_blank');
  };


  const registerTemplate = async (templateInfo) => {
    try {
      setRegistering(true);
      
      // Register template from existing file
      const response = await axios.post('/api/v1/pdf/templates/register-from-file', {
        templateId: templateInfo.templateId,
        name: templateInfo.name,
        description: templateInfo.description,
        documentType: templateInfo.documentType,
        department: templateInfo.department,
        version: templateInfo.version,
        filePath: templateInfo.filePath,
        cssFilePath: templateInfo.cssFilePath,
        allowedRoles: [ROLES.SPECTATOR],
        allowedDepartments: [templateInfo.department],
        isDefault: false
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      showToast(`Template "${templateInfo.name}" registered successfully`, 'success');
      fetchTemplates();
      return response.data.template;
    } catch (error) {
      console.error('Error registering template:', error);
      const errorMessage = error.response?.data?.message || 'Failed to register template';
      if (error.response?.status === 409) {
        showToast(`Template already exists: ${templateInfo.name}`, 'info');
      } else {
        showToast(errorMessage, 'error');
      }
      throw error;
    } finally {
      setRegistering(false);
    }
  };

  const handleRegisterAll = async () => {
    const unregistered = availableTemplates.filter(t => 
      !templates.some(reg => reg.templateId === t.templateId && reg.version === t.version)
    );

    if (unregistered.length === 0) {
      showToast('All templates are already registered', 'info');
      return;
    }

    if (!window.confirm(`Register ${unregistered.length} template(s)?`)) {
      return;
    }

    try {
      setRegistering(true);
      const response = await axios.post('/api/v1/pdf/templates/register-all', {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const registered = response.data.results.filter(r => r.status === 'registered').length;
      const existing = response.data.results.filter(r => r.status === 'already_exists').length;
      const errors = response.data.errors?.length || 0;

      if (registered > 0) {
        showToast(`Successfully registered ${registered} template(s)`, 'success');
      }
      if (existing > 0) {
        showToast(`${existing} template(s) were already registered`, 'info');
      }
      if (errors > 0) {
        showToast(`${errors} template(s) failed to register`, 'error');
      }

      fetchTemplates();
    } catch (error) {
      console.error('Error registering all templates:', error);
      showToast(error.response?.data?.message || 'Failed to register templates', 'error');
    } finally {
      setRegistering(false);
    }
  };

  const handleTestTemplate = (template) => {
    setSelectedTemplate(template);
    setTestDialogOpen(true);
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete template "${template.name}" (${template.templateId} v${template.version})?\n\nThis will deactivate the template.`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `/api/v1/pdf/templates/${template.templateId}/${template.version}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      showToast(`Template "${template.name}" deleted successfully`, 'success');
      fetchTemplates(); // Refresh the list
    } catch (error) {
      console.error('Error deleting template:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete template';
      showToast(errorMessage, 'error');
    }
  };

  const isRegistered = (templateId, version) => {
    return templates.some(t => t.templateId === templateId && t.version === version);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">PDF Template Testing</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTemplates}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleRegisterAll}
            disabled={registering}
          >
            Register All Templates
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Testing Area for Spectator:</strong> Register and test PDF templates here. 
          Once confirmed working, templates will be moved to their proper locations.
        </Typography>
      </Alert>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Available Templates" />
        <Tab label="Registered Templates" />
        <Tab label="Generated PDFs" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Available Template Files
          </Typography>
          <Grid container spacing={2}>
            {availableTemplates.map((template) => {
              const registered = isRegistered(template.templateId, template.version);
              return (
                <Grid item xs={12} md={6} key={template.templateId}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box>
                          <Typography variant="h6">{template.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </Box>
                        {registered && (
                          <Chip label="Registered" color="success" size="small" />
                        )}
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Template ID:</Typography>
                          <Typography variant="body2">{template.templateId}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Document Type:</Typography>
                          <Typography variant="body2">{template.documentType}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Department:</Typography>
                          <Typography variant="body2">{template.department}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Version:</Typography>
                          <Typography variant="body2">{template.version}</Typography>
                        </Box>
                      </Stack>
                      <Box mt={2}>
                        <Button
                          variant={registered ? "outlined" : "contained"}
                          size="small"
                          startIcon={registered ? <ViewIcon /> : <UploadIcon />}
                          onClick={() => registered ? handleTestTemplate(templates.find(t => t.templateId === template.templateId)) : registerTemplate(template)}
                          disabled={registering}
                          fullWidth
                        >
                          {registered ? 'View/Test' : 'Register Template'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Registered Templates ({templates.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No templates registered yet
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {template.templateId}
                        </Typography>
                      </TableCell>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Chip label={template.documentType} size="small" />
                      </TableCell>
                      <TableCell>{template.department?.replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Chip label={template.version} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={template.isActive ? 'success' : 'default'}
                        />
                        {template.isDefault && (
                          <Chip label="Default" size="small" color="secondary" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Test Template">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleTestTemplate(template)}
                          >
                            <TestIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const token = localStorage.getItem('token');
                              const url = `${window.location.origin}/api/v1/pdf/templates/${template.templateId}/${template.version}?token=${encodeURIComponent(token)}`;
                              window.open(url, '_blank');
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview Template">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              const token = localStorage.getItem('token');
                              const url = `${window.location.origin}/api/v1/pdf/templates/${template.templateId}/${template.version}/preview?token=${encodeURIComponent(token)}`;
                              setSelectedTemplate(template);
                              setPreviewUrl(url);
                              setPreviewDialogOpen(true);
                            }}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Template">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTemplate(template)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Generated PDFs ({generatedPDFs.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchGeneratedPDFs}
              disabled={pdfsLoading}
            >
              Refresh
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Generated By</TableCell>
                  <TableCell>Generated At</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pdfsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : generatedPDFs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No PDFs generated yet
                    </TableCell>
                  </TableRow>
                ) : (
                  generatedPDFs.map((pdf) => (
                    <TableRow key={pdf._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {pdf.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={pdf.documentType} size="small" />
                      </TableCell>
                      <TableCell>{pdf.referenceNumber || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{pdf.templateName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          v{pdf.templateVersion}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pdf.generatedBy ? (
                          <Box>
                            <Typography variant="body2">{pdf.generatedBy.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pdf.generatedBy.email}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(pdf.generatedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {(pdf.fileSize / 1024).toFixed(1)} KB
                        {pdf.pageCount && ` (${pdf.pageCount} pages)`}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewPDF(pdf._id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadPDF(pdf._id)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Preview Template Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          Template Preview: {selectedTemplate?.name || 'Template Preview'}
          <Button
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 64px)', overflow: 'hidden' }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="Template Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Test Template Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Template: {selectedTemplate?.name}</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                To test this template, use the "Create from Template" feature in the Field Mappings page
                or generate a PDF using the PDF generation API with sample data.
              </Typography>
            </Alert>
          )}
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Template Information</Typography>
              <Typography variant="body2"><strong>ID:</strong> {selectedTemplate?.templateId}</Typography>
              <Typography variant="body2"><strong>Version:</strong> {selectedTemplate?.version}</Typography>
              <Typography variant="body2"><strong>Document Type:</strong> {selectedTemplate?.documentType}</Typography>
              <Typography variant="body2"><strong>Department:</strong> {selectedTemplate?.department}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Next Steps</Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Go to "PDF Field Mappings" page</li>
                <li>Click "Create from Template"</li>
                <li>Select this template to analyze and create field mappings</li>
                <li>Or use the PDF generation API to test with sample data</li>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = '/pdf/field-mappings';
            }}
          >
            Go to Field Mappings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateTesting;

